import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import asyncService from '../../../../../datahub/asyncService';
import selectUser from '../../../../store/selectors/appSelector';
import { showSnackbar } from '../../../app';

/**
 * Small helper hook to keep API/side-effect logic out of the view + form components.
 * It creates day jobs (job orders) using the existing asyncService helper.
 */
const useCreateRecurringTwoWeeksDayJobs = ({ onSuccess } = {}) => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  const [isCreatingJobs, setIsCreatingJobs] = useState(false);

  const createJobOrders = useCallback(
    (payload) => {
      if (!payload || !payload.jobOrders || payload.jobOrders.length === 0) {
        dispatch(showSnackbar({ message: 'job.create.validation.invalidJobOrderData' }));
        return;
      }

      setIsCreatingJobs(true);

      asyncService({
        httpMethod: 'POST',
        route: 'v2/create-job-order',
        data: payload,
        onSuccess: (response) => {
          const jobOrderIds = response?.data?.jobOrderIds;
          const hasJobOrderIds = Array.isArray(jobOrderIds) && jobOrderIds.length > 0;
          if (hasJobOrderIds) {
            dispatch(showSnackbar({ message: 'job.create.recurringTwoWeeks.success' }));
            // Ensure loading state is always cleared for reusability (even if caller navigates away).
            setIsCreatingJobs(false);
            onSuccess?.(response);
            return;
          }

          dispatch(showSnackbar({ message: 'Failed to create job orders' }));
          setIsCreatingJobs(false);
        },
        onError: (error) => {
          const errorMessage =
            error?.response?.data?.message || error?.message || 'Failed to create job orders';
          dispatch(showSnackbar({ message: errorMessage }));
          setIsCreatingJobs(false);
        },
        dispatch,
        token: user?.token,
        refreshToken: user?.refreshToken,
      });
    },
    [dispatch, onSuccess, user?.refreshToken, user?.token]
  );

  return { createJobOrders, isCreatingJobs };
};

export default useCreateRecurringTwoWeeksDayJobs;
